import { useEffect, useMemo } from 'react';

import { useBxpWebStargateClient } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import { checkFeatureFlagGate } from '@wac-web/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@wac-web/utils/feature-flags/feature-flag-manifest';

import { getOrgQueryParam } from '@/common/utils/get-org-query-param';
import {
	useFastshiftDispatch,
	useFastshiftSelector,
	FastshiftDispatchPromise,
	TABLE_IDS,
} from '@/state';
import * as StateActions from '@/state/actions';

/**
 * Fetches support requests data with deduplication and abort cleanup.
 * Prevents redundant API calls by checking lastFetchParams.
 */
export const useFetchSupportRequests = () => {
	const dispatch = useFastshiftDispatch();
	const stargateClient = useBxpWebStargateClient();
	const isOrganizationRedesignEnabled = checkFeatureFlagGate(
		featureFlags.sxpFspOrganizationRedesign,
	);

	const { data: customerDomainData } = useFastshiftSelector((state) => state.customerDomain);

	const selectedDomainOrg = useFastshiftSelector((state) => state.selectedDomainOrg);
	const requestDomainOrg = isOrganizationRedesignEnabled ? null : selectedDomainOrg;

	const { lastFetchParams } = useFastshiftSelector((state) => state.supportRequests);

	const supportTicketQueryParams = useMemo(() => {
		return requestDomainOrg ? getOrgQueryParam(requestDomainOrg) : {};
	}, [requestDomainOrg]);

	useEffect(() => {
		let supportRequestsPromise: FastshiftDispatchPromise<unknown> | null = null;

		if (customerDomainData && (isOrganizationRedesignEnabled || requestDomainOrg)) {
			const needsFetch =
				!lastFetchParams ||
				lastFetchParams.customerDomainId !== customerDomainData.customerDomainId ||
				lastFetchParams.queryParams.orgId !== supportTicketQueryParams.orgId ||
				lastFetchParams.queryParams.permissionLevel !== supportTicketQueryParams.permissionLevel;

			if (!needsFetch) {
				return;
			}

			// Clear migration tickets table controls when fetching new data (org changed)
			dispatch(
				StateActions.clearTableControls({
					tableId: TABLE_IDS.MIGRATION_TICKETS,
				}),
			);

			supportRequestsPromise = dispatch(
				StateActions.getFastshiftSupportRequests({
					stargateClient,
					customerDomainId: customerDomainData.customerDomainId,
					queryParams: supportTicketQueryParams,
				}),
			);
		}

		return () => {
			if (supportRequestsPromise) {
				supportRequestsPromise.abort();
			}
		};
	}, [
		dispatch,
		stargateClient,
		customerDomainData,
		isOrganizationRedesignEnabled,
		requestDomainOrg,
		supportTicketQueryParams,
		lastFetchParams,
	]);
};
